import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { apiClient } from '../config/apiConfig';
import { getLogger } from '../components/features/Campaigns/dashboard/config/environmentConfig';

const logger = getLogger('log');

// Create the context
const WorkloadContext = createContext();

/**
 * Simplified Workload Provider Component
 * 
 * Loads all creator workload data at startup and provides lookup by creator ID.
 * Workload is stored in the Creator model and updated when campaign events occur.
 */
export const WorkloadProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workloadData, setWorkloadData] = useState(new Map());
  const [isInitialized, setIsInitialized] = useState(false);
  
  /**
   * Calculate workload level based on active campaigns
   */
  const calculateWorkloadLevel = useCallback((activeCampaigns) => {
    if (activeCampaigns === 0) return 'low';
    if (activeCampaigns <= 2) return 'low';
    if (activeCampaigns <= 4) return 'medium';
    if (activeCampaigns <= 6) return 'high';
    return 'overloaded';
  }, []);
  
  /**
   * Load all creator workloads from the API
   * This is called once on mount to populate the workload map
   */
  const loadAllWorkloads = useCallback(async () => {
    try {
      setLoading(true);
      logger('Loading all creator workloads...');
      
      const response = await apiClient.get('/creators/workload');
      
      logger('Workload API response:', response.data);
      
      if (response.data && response.data.success && response.data.data) {
        const newWorkloadMap = new Map();
        
        // Process all workload data
        response.data.data.forEach(item => {
          if (item.creatorId) {
            const processedData = {
              activeCampaigns: item.activeCampaigns || 0,
              totalCampaigns: item.totalCampaigns || 0,
              workloadLevel: item.workloadLevel || calculateWorkloadLevel(item.activeCampaigns || 0),
              lastUpdated: item.lastUpdated
            };
            newWorkloadMap.set(item.creatorId, processedData);
            
            // Log high workload creators for debugging
            if (item.activeCampaigns > 2) {
              logger(`Creator ${item.creatorId}: ${item.activeCampaigns} campaigns, level: ${processedData.workloadLevel}`);
            }
          }
        });
        
        logger(`Loaded workload data for ${newWorkloadMap.size} creators`);
        setWorkloadData(newWorkloadMap);
        setIsInitialized(true);
      } else {
        logger('No workload data returned from API');
      }
    } catch (err) {
      logger('Error loading all workloads:', err);
      setError('Failed to load creator workloads');
    } finally {
      setLoading(false);
    }
  }, [calculateWorkloadLevel]);
  
  /**
   * Load all workloads on mount
   */
  useEffect(() => {
    loadAllWorkloads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  /**
   * Fetch workload data for a specific creator from API
   * Data comes from Creator.workload field in database
   */
  const fetchCreatorWorkload = useCallback(async (creatorId) => {
    if (!creatorId) {
      logger('No creator ID provided for workload fetch');
      return null;
    }
    
    try {
      logger(`Fetching workload data for creator ${creatorId}`);
      
      const response = await apiClient.get(`/creators/${creatorId}/workload`);
      
      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        logger(`Successfully fetched workload for creator ${creatorId}:`, data);
        
        // Standardized format
        const processedData = {
          activeCampaigns: data.activeCampaigns || data.workload || 0,
          totalCampaigns: data.totalCampaigns || data.workload || 0,
          workloadLevel: data.workloadLevel || calculateWorkloadLevel(data.activeCampaigns || data.workload || 0),
          lastUpdated: data.lastUpdated
        };
        
        // Store in state
        setWorkloadData(prev => {
          const newMap = new Map(prev);
          newMap.set(creatorId, processedData);
          return newMap;
        });
        
        return processedData;
      }
      
      return null;
    } catch (err) {
      logger(`Error fetching workload data for creator ${creatorId}:`, err);
      setError(`Failed to fetch workload for creator ${creatorId}`);
      return null;
    }
  }, [calculateWorkloadLevel]);
  
  /**
   * Fetch workload data for multiple creators
   * Batch endpoint that queries Creator.workload from database
   */
  const fetchMultipleCreatorWorkloads = useCallback(async (creatorIds) => {
    // Validate and filter creator IDs
    if (!Array.isArray(creatorIds) || creatorIds.length === 0) {
      logger('No creator IDs provided for batch workload fetch');
      return new Map();
    }
    
    // Filter out invalid IDs (MongoDB ObjectIds are 24 characters)
    const validIds = creatorIds.filter(id => 
      id && 
      typeof id === 'string' && 
      id.length === 24 && 
      /^[a-fA-F0-9]{24}$/.test(id)
    );
    
    if (validIds.length === 0) {
      logger('No valid creator IDs after filtering');
      return new Map();
    }
    
    creatorIds = validIds;
    
    setLoading(true);
    setError(null);
    
    try {
      logger(`Fetching workload data for ${creatorIds.length} creators:`, creatorIds);
      
      const response = await apiClient.post('/creators/workload/batch', {
        creatorIds: creatorIds
      });
      
      logger('API Response:', response);
      
      const results = new Map();
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        logger('Processing batch response:', {
          responseLength: response.data.data.length,
          firstItem: response.data.data[0],
          fullResponse: response.data.data
        });
        
        response.data.data.forEach(item => {
          if (item.creatorId) {
            const processedData = {
              activeCampaigns: item.activeCampaigns || item.workload || 0,
              totalCampaigns: item.totalCampaigns || item.workload || 0,
              workloadLevel: item.workloadLevel || calculateWorkloadLevel(item.activeCampaigns || item.workload || 0),
              lastUpdated: item.lastUpdated
            };
            results.set(item.creatorId, processedData);
            logger(`Stored workload for ${item.creatorId}:`, {
              rawItem: item,
              processedData
            });
          }
        });
        
        logger(`Successfully fetched workload data for ${results.size} creators, keys:`, Array.from(results.keys()));
        
        // Store all in state
        setWorkloadData(prev => {
          const newMap = new Map(prev);
          results.forEach((data, creatorId) => {
            newMap.set(creatorId, data);
          });
          logger('Updated workloadData state:', {
            previousSize: prev.size,
            newSize: newMap.size,
            newKeys: Array.from(newMap.keys()).slice(0, 5)
          });
          return newMap;
        });
      }
      
      return results;
    } catch (err) {
      logger('Error fetching multiple creator workloads:', err);
      setError('Failed to fetch creator workloads');
      return new Map();
    } finally {
      setLoading(false);
    }
  }, [calculateWorkloadLevel]);
  
  /**
   * Get workload from state
   */
  const getCreatorWorkload = useCallback((creatorId) => {
    const data = workloadData.get(creatorId);
    logger(`Getting workload for ${creatorId}:`, {
      hasData: !!data,
      mapSize: workloadData.size,
      keysInMap: Array.from(workloadData.keys()).slice(0, 5),
      requestedKey: creatorId,
      foundData: data
    });
    return data || null;
  }, [workloadData]);
  
  /**
   * Clear all workload data
   */
  const clearCache = useCallback(() => {
    setWorkloadData(new Map());
  }, []);
  
  // Memoized context value
  const value = useMemo(() => ({
    loading,
    error,
    isInitialized,
    fetchCreatorWorkload,
    fetchMultipleCreatorWorkloads,
    getCreatorWorkload,
    clearCache,
    refreshAllWorkloads: loadAllWorkloads
  }), [
    loading,
    error,
    isInitialized,
    fetchCreatorWorkload,
    fetchMultipleCreatorWorkloads,
    getCreatorWorkload,
    clearCache,
    loadAllWorkloads
  ]);
  
  return (
    <WorkloadContext.Provider value={value}>
      {children}
    </WorkloadContext.Provider>
  );
};

/**
 * Custom React hook for accessing Creator Workload Context
 */
export const useWorkload = () => {
  const context = useContext(WorkloadContext);
  
  if (!context) {
    throw new Error('useWorkload must be used within a WorkloadProvider');
  }
  
  return context;
};

export default WorkloadContext;